import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getAllSegments,
  addSegment,
  updateSegment,
  deleteSegment,
} from "../../../../../../../services/segmentDossierServices";

import {
  addFichier,
  updateFichier,
  deleteFichier,
} from "../../../../../../../services/fichierDossierServices";

import {
  EMPTY_FILE_FORM,
  EMPTY_SEGMENT_FORM,
  buildFilePayload,
  buildSegmentPayload,
  calculateDossierStats,
  extractCreatedSegmentId,
  filterSegments,
  getNextSegmentOrder,
  sortSegments,
  validateFilePayload,
  validateSegmentPayload,
} from "../utils/dossierInscriptionUtils";

export function useDossierInscriptionSettings() {
  const [loading, setLoading] = useState(true);
  const [segments, setSegments] = useState([]);

  const [activeSegmentId, setActiveSegmentId] = useState(null);
  const [search, setSearch] = useState("");

  const [fileForms, setFileForms] = useState({});
  const [fileToEditId, setFileToEditId] = useState({});

  const [savingSegmentId, setSavingSegmentId] = useState(null);
  const [deletingSegmentId, setDeletingSegmentId] = useState(null);
  const [savingFileId, setSavingFileId] = useState(null);
  const [deletingFileId, setDeletingFileId] = useState(null);

  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [creatingSegment, setCreatingSegment] = useState(false);
  const [newSegment, setNewSegment] = useState(EMPTY_SEGMENT_FORM);
  const [newFormKey, setNewFormKey] = useState(0);

  const fetchSegments = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getAllSegments();
      const sorted = sortSegments(data);

      setSegments(sorted);

      setFileForms((prev) => {
        const next = { ...prev };

        sorted.forEach((segment) => {
          if (!next[segment.id]) {
            next[segment.id] = { ...EMPTY_FILE_FORM };
          }
        });

        return next;
      });

      setActiveSegmentId((current) => {
        if (current && sorted.some((segment) => segment.id === current)) {
          return current;
        }

        return sorted[0]?.id || null;
      });
    } catch (error) {
      console.error("Erreur chargement dossier inscription :", error);
      toast.error("Impossible de charger les segments du dossier.");
      setSegments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSegments();
  }, [fetchSegments]);

  const filteredSegments = useMemo(() => {
    return filterSegments(segments, search);
  }, [segments, search]);

  const activeSegment = useMemo(() => {
    return segments.find((segment) => segment.id === activeSegmentId) || null;
  }, [segments, activeSegmentId]);

  const stats = useMemo(() => {
    return calculateDossierStats(segments);
  }, [segments]);

  const openCreateDrawer = () => {
    setNewSegment({
      ...EMPTY_SEGMENT_FORM,
      ordre_segment: getNextSegmentOrder(segments),
    });
    setNewFormKey((value) => value + 1);
    setCreateDrawerOpen(true);
  };

  const closeCreateDrawer = () => {
    if (creatingSegment) return;
    setCreateDrawerOpen(false);
  };

  const resetNewSegment = () => {
    setNewSegment({
      ...EMPTY_SEGMENT_FORM,
      ordre_segment: getNextSegmentOrder(segments),
    });
    setNewFormKey((value) => value + 1);
  };

  const handleAddSegment = async () => {
    const payload = buildSegmentPayload(newSegment);
    const errorMessage = validateSegmentPayload(payload);

    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    try {
      setCreatingSegment(true);

      const created = await addSegment(payload);
      const createdId = extractCreatedSegmentId(created);

      toast.success("Segment ajouté.");

      setCreateDrawerOpen(false);
      resetNewSegment();

      await fetchSegments();

      if (createdId) {
        setActiveSegmentId(createdId);
      }
    } catch (error) {
      console.error("Erreur création segment :", error);
      toast.error("Erreur lors de l’ajout du segment.");
    } finally {
      setCreatingSegment(false);
    }
  };

  const handleLocalSegmentChange = (id, field, value) => {
    setSegments((prev) =>
      prev.map((segment) =>
        segment.id === id ? { ...segment, [field]: value } : segment
      )
    );
  };

  const handleSaveSegment = async (id) => {
    const segment = segments.find((item) => item.id === id);
    if (!segment) return;

    const payload = buildSegmentPayload(segment);
    const errorMessage = validateSegmentPayload(payload);

    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    try {
      setSavingSegmentId(id);

      await updateSegment(id, payload);

      toast.success("Segment enregistré.");
      await fetchSegments();
    } catch (error) {
      console.error("Erreur sauvegarde segment :", error);
      toast.error("Erreur lors de l’enregistrement du segment.");
    } finally {
      setSavingSegmentId(null);
    }
  };

  const handleDeleteSegment = async (id) => {
    const segment = segments.find((item) => item.id === id);

    if (
      !window.confirm(
        `Supprimer le segment « ${segment?.segment_name || "sans nom"} » ?`
      )
    ) {
      return;
    }

    try {
      setDeletingSegmentId(id);

      await deleteSegment(id);

      toast.success("Segment supprimé.");

      const remaining = segments.filter((item) => item.id !== id);
      setActiveSegmentId(remaining[0]?.id || null);

      await fetchSegments();
    } catch (error) {
      console.error("Erreur suppression segment :", error);
      toast.error("Erreur lors de la suppression du segment.");
    } finally {
      setDeletingSegmentId(null);
    }
  };

  const handleFileChange = (segmentId, field, value) => {
    setFileForms((prev) => ({
      ...prev,
      [segmentId]: {
        ...(prev[segmentId] || EMPTY_FILE_FORM),
        [field]: value,
      },
    }));
  };

  const handleEditFile = (file, segmentId) => {
    setFileForms((prev) => ({
      ...prev,
      [segmentId]: {
        id: file.id,
        nom_fichier: file.nom_fichier || "",
        lien_fichier: file.lien_fichier || "",
        detail_fichier: file.detail_fichier || "",
      },
    }));

    setFileToEditId((prev) => ({
      ...prev,
      [segmentId]: file.id,
    }));
  };

  const resetFileForm = (segmentId) => {
    setFileForms((prev) => ({
      ...prev,
      [segmentId]: { ...EMPTY_FILE_FORM },
    }));

    setFileToEditId((prev) => ({
      ...prev,
      [segmentId]: null,
    }));
  };

  const handleSaveFile = async (segmentId) => {
    const form = fileForms[segmentId] || EMPTY_FILE_FORM;
    const payload = buildFilePayload(form, segmentId);
    const errorMessage = validateFilePayload(payload);

    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    try {
      setSavingFileId(payload.id || `new-${segmentId}`);

      if (payload.id) {
        await updateFichier(payload.id, payload);
        toast.success("Fichier modifié.");
      } else {
        await addFichier(payload);
        toast.success("Fichier ajouté.");
      }

      resetFileForm(segmentId);
      await fetchSegments();
    } catch (error) {
      console.error("Erreur sauvegarde fichier :", error);
      toast.error("Erreur lors de la sauvegarde du fichier.");
    } finally {
      setSavingFileId(null);
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm("Supprimer ce fichier ?")) return;

    try {
      setDeletingFileId(fileId);

      await deleteFichier(fileId);

      toast.success("Fichier supprimé.");
      await fetchSegments();
    } catch (error) {
      console.error("Erreur suppression fichier :", error);
      toast.error("Erreur lors de la suppression du fichier.");
    } finally {
      setDeletingFileId(null);
    }
  };

  return {
    loading,
    segments,
    filteredSegments,
    activeSegment,
    activeSegmentId,
    setActiveSegmentId,
    search,
    setSearch,

    stats,

    fileForms,
    fileToEditId,

    savingSegmentId,
    deletingSegmentId,
    savingFileId,
    deletingFileId,

    createDrawerOpen,
    creatingSegment,
    newSegment,
    setNewSegment,
    newFormKey,

    openCreateDrawer,
    closeCreateDrawer,
    resetNewSegment,

    fetchSegments,
    handleAddSegment,
    handleLocalSegmentChange,
    handleSaveSegment,
    handleDeleteSegment,

    handleFileChange,
    handleEditFile,
    resetFileForm,
    handleSaveFile,
    handleDeleteFile,
  };
}